import { QueryResult, useQuery } from '@apollo/client';

import { PageInfo, QuotesFilters } from '__generated__/GQL';
import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { logEvent } from 'features/Analytics/analytics';

import {
  QuotesDocument,
  QuotesQuery,
  QuotesQueryVariables,
} from './graphql/quotes.gql';

export type QuoteFromList =
  QuotesQuery['viewer']['company']['quotes']['edges'][0]['node'];

export interface GetQuotesHookResult
  extends Pick<
    QueryResult<QuotesQuery, QuotesQueryVariables>,
    'loading' | 'error' | 'refetch' | 'networkStatus'
  > {
  quotes: QuoteFromList[];
  fetchMore: () => void;
  pageInfo?: PageInfo;
}

export const QUOTES_PER_PAGE = 20;

export const useGetQuotes = (filters: QuotesFilters): GetQuotesHookResult => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is undefined in useGetQuotes');
  }

  const { data, fetchMore, ...rest } = useQuery(QuotesDocument, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (filters?.searchInput) {
        logEvent({
          name: 'Quotes Keyword Searched',
          usePrefix: false,
        });
      }
    },
    variables: {
      after: '',
      companyProfileId,
      filters: filters ?? null,
      first: QUOTES_PER_PAGE,
    },
  });

  const quotes = data?.viewer?.company?.quotes?.edges || [];
  const pageInfo = data?.viewer?.company?.quotes?.pageInfo;

  const fetchMoreQuotes = async () => {
    await fetchMore({
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeConnectionResultsBy('quoteId')(prevResult, fetchMoreResult),
      variables: {
        after: pageInfo?.endCursor,
      },
    });
  };

  return {
    fetchMore: fetchMoreQuotes,
    pageInfo,
    quotes: quotes.map(({ node }) => node),
    ...rest,
  };
};
